// "Load older" paging math, shared by every surface that pages back through the retention
// archive beyond its main window: the flat /inbox, the pop-out's shared-mailbox pane, the
// pop-out's My Mail pane, and (v1.5.66) the account page's Shared Inbox tab.
//
// This was copy-pasted three times before v1.5.66 and the copies had quietly diverged — each
// one was right about something the others got wrong. The reconciliation is recorded here
// because every one of these rules exists to stop a specific failure:
//
//   * the cursor is the minimum over EVERYTHING the surface holds, including previously
//     loaded filtered/noise rows. Two of the three copies used "loaded older rows if any,
//     else the base rows", which quietly assumes older pages are always strictly older than
//     the base — true today, but it silently skips mail if a locally-held filtered row is
//     older than the oldest visible one.
//   * null receivedDateTime is SKIPPED, never used as a cursor. Ghost rows with a null
//     received_at are real (v1.3.49) and are precisely why readMailboxEmailsFromDb orders
//     with nullsFirst:false. One copy had no guard, so a ghost produced an invalid cursor
//     and the server 400'd.
//   * a page is exhausted only when BOTH buckets are empty. A page that is entirely
//     noise-filtered has zero visible messages but is NOT the end of the archive; two of the
//     three copies would have shown "No older emails" and stopped paging.

/** The smallest usable field set — deliberately structural so this module imports nothing. */
export type DatedRow = { receivedDateTime?: string | null };
export type IdentifiedRow = { id: string };

/**
 * ISO timestamp to page back from: the oldest row the surface currently holds.
 *
 * Rows with no date are skipped rather than treated as oldest — see the ghost-row note
 * above. Returns null when nothing is datable, which callers must treat as "cannot page"
 * (not as "exhausted"): a surface with no rows yet has no cursor to page from.
 */
export function oldestCursor(rows: readonly DatedRow[]): string | null {
  let oldest: string | null = null;
  for (const r of rows) {
    const at = r.receivedDateTime;
    if (!at) continue;
    if (oldest === null || at < oldest) oldest = at;
  }
  return oldest;
}

/**
 * Append a freshly fetched page, dropping anything already known.
 *
 * The id dedupe guards the window-shift case: new mail arriving between the first fetch and
 * the paging click moves the window forward, so a page can overlap rows the surface already
 * has. It does NOT collapse duplicate *twins* — those are distinct graph ids by definition
 * and are collapsed server-side per response (server/lib/emailDupes.ts). A twin pair split
 * across a page boundary therefore survives as two rows; that gap is pre-existing and
 * documented at the /api/inbox/older handler.
 */
export function mergeOlderPage<T extends IdentifiedRow>(
  existing: readonly T[],
  page: readonly T[],
  knownIds: ReadonlySet<string>,
): T[] {
  const seen = new Set(knownIds);
  for (const r of existing) seen.add(r.id);
  const fresh: T[] = [];
  for (const r of page) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    fresh.push(r);
  }
  return fresh.length === 0 ? (existing as T[]) : [...existing, ...fresh];
}

/**
 * True when the archive holds nothing older than the cursor.
 *
 * Both buckets must be empty: an all-noise page is not the end of the archive. `filtered` is
 * only populated on the personal-mailbox branch of /api/inbox/older today, so for shared
 * mailboxes this reduces to "no messages" — but encoding the rule here means a future
 * shared-side noise partition cannot silently re-introduce a premature stop.
 */
export function isPageExhausted(page: {
  messages: readonly unknown[];
  filtered?: readonly unknown[] | null;
}): boolean {
  return page.messages.length === 0 && (page.filtered?.length ?? 0) === 0;
}

// ---------------------------------------------------------------------------------------
// Paging state transitions (ENG-114).
//
// These used to run INSIDE setState updater callbacks in useLoadOlder, with the hook reading
// a closure the updater filled in ("which mailboxes do I fetch?", "how many rows landed?").
// React 19 only runs an updater synchronously when the component has no pending update;
// otherwise it defers it to the next render. Deferred, the fetch list was still empty when
// the hook read it, so it returned without fetching, and then the updater ran at render and
// set loading=true with no request in flight to ever clear it. That is the "Load older
// spins forever" report: reliably reproducible on the pop-out, which re-renders constantly.
// Pure transitions let the hook decide synchronously from the state it last committed.

export type OlderPageResult<T> = { messages: T[]; filtered?: T[] | null };

export type OlderBag<T> = {
  rows: Record<string, T[]>;
  filtered: Record<string, T[]>;
  loading: Record<string, boolean>;
  exhausted: Record<string, boolean>;
  version: number;
};

export const EMPTY_OLDER_BAG: OlderBag<never> = Object.freeze({
  rows: {},
  filtered: {},
  loading: {},
  exhausted: {},
  version: 0,
}) as OlderBag<never>;

export type OlderTarget = {
  mailbox: string;
  /** Server-owned rows for this mailbox -- the cursor floor before any page is loaded. */
  baseRows: readonly (IdentifiedRow & DatedRow)[];
};

/**
 * Decide what to fetch and mark it loading. Invariant: a mailbox is marked loading if and
 * only if it is in `pending` -- loading with no request behind it is the ENG-114 spinner.
 * Returns the same bag (by reference) when there is nothing to do.
 */
export function planOlderFetch<T extends DatedRow>(
  prev: OlderBag<T>,
  targets: readonly OlderTarget[],
): { pending: Array<{ mailbox: string; before: string }>; next: OlderBag<T> } {
  const pending: Array<{ mailbox: string; before: string }> = [];
  const loading = { ...prev.loading };
  for (const t of targets) {
    if (prev.loading[t.mailbox] || prev.exhausted[t.mailbox]) continue;
    if (pending.some((p) => p.mailbox === t.mailbox)) continue;
    // Cursor over EVERYTHING held for this mailbox: base rows plus any previously loaded
    // page, visible or filtered. See the cursor rule at the top of this file.
    const held = [
      ...t.baseRows,
      ...(prev.rows[t.mailbox] ?? []),
      ...(prev.filtered[t.mailbox] ?? []),
    ];
    const before = oldestCursor(held);
    if (!before) continue;
    pending.push({ mailbox: t.mailbox, before });
    loading[t.mailbox] = true;
  }
  if (pending.length === 0) return { pending, next: prev };
  return { pending, next: { ...prev, loading, version: prev.version + 1 } };
}

/**
 * Fold fetched pages back in. Every mailbox in `results` stops loading, whether its fetch
 * succeeded (page) or failed (null). `added` counts newly visible rows so a caller holding a
 * render cap can widen it.
 */
export function applyOlderPages<T extends IdentifiedRow>(
  prev: OlderBag<T>,
  targets: readonly { mailbox: string; baseRows: readonly IdentifiedRow[] }[],
  results: readonly { mailbox: string; page: OlderPageResult<T> | null }[],
): { next: OlderBag<T>; added: number } {
  const rows = { ...prev.rows };
  const filtered = { ...prev.filtered };
  const loading = { ...prev.loading };
  const exhausted = { ...prev.exhausted };
  let added = 0;
  for (const r of results) {
    loading[r.mailbox] = false;
    if (!r.page) continue;
    if (isPageExhausted(r.page)) {
      exhausted[r.mailbox] = true;
      continue;
    }
    const target = targets.find((t) => t.mailbox === r.mailbox);
    const knownIds = new Set((target?.baseRows ?? []).map((m) => m.id));
    const beforeRows = rows[r.mailbox] ?? [];
    const nextRows = mergeOlderPage(beforeRows, r.page.messages, knownIds);
    added += nextRows.length - beforeRows.length;
    rows[r.mailbox] = nextRows;
    filtered[r.mailbox] = mergeOlderPage(filtered[r.mailbox] ?? [], r.page.filtered ?? [], knownIds);
  }
  return { next: { rows, filtered, loading, exhausted, version: prev.version + 1 }, added };
}
